/**
 * Soft≠Live Page194 — Fleet shell residual liquid (tab rail ∥ solo record).
 * Mount: /fleet/* shell chrome
 * Buses (contract ops — never painted in operator DOM):
 *   get__fleet_status (always-on posture · dual tab_rail ∥ solo_record)
 * Child tab desks retain their own status SoTs (health/groups/…/group detail).
 * Soft residual deleted:
 *  - Soft all-tabs invent without posture seat
 *  - Soft crumb recover when solo bus closed
 *  - Soft firmware/external open for user-floor seats
 *  - Soft open-all invent while posture is still loading
 *  - path /operation labels painted in DOM
 * Operator copy stays path-free (Refresh only — no engineer status chips).
 */
import { useMemo } from "react";
import { AnimatePresence } from "framer-motion";
import { html, M, F, Ico, _SPRING, _FADE, go, Alert } from "./ui.js";
import { path, useApi } from "./api.js";
import { Device } from "./fleet-device-desk.js";
import { GroupPage } from "./fleet-group-desk.js";
import { ExternalFleetDesk } from "./external-fleet-desk.js";
import { FleetDiagnoseDesk } from "./fleet-diagnose-desk.js";
import { FleetHealthDesk } from "./fleet-health-desk.js";
import { FleetGroupsDesk } from "./fleet-groups-desk.js";
import { FleetZonesDesk } from "./fleet-zones-desk.js";
import { FleetFirmwareDesk } from "./fleet-firmware-desk.js";

const TABS = [
  { id: "firmware", label: "Board updates", flag: "firmwareTabAllowed" },
  { id: "groups", label: "Groups", flag: "groupsTabAllowed" },
  { id: "zones", label: "Zones", flag: "zonesTabAllowed" },
  { id: "health", label: "Fleet health", flag: "healthTabAllowed" },
  { id: "diagnose", label: "Why offline", flag: "diagnoseTabAllowed" },
  { id: "external", label: "External / SEB", flag: "externalTabAllowed" },
];

/** Path-free operator copy for Fleet shell failures. */
export function softFleetShellErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That action could not be completed.";
  if (/\b403\b|forbidden|seat_gated|not_authorized|denied/.test(low)) {
    return "Your account cannot open this Fleet seat. Ask a manager if you need access.";
  }
  if (/\b401\b|session|unauthorized|missing bearer/.test(low)) {
    return "Sign in again to continue.";
  }
  if (/\b404\b|not found/.test(low)) {
    return "That Fleet seat is not available.";
  }
  if (/\b429\b|too many/.test(low)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|unavailable/.test(low)) {
    return "Fleet is not ready right now.";
  }
  if (low === "ops_dashboard_seat_closed") {
    return "Fleet health metrics need an operations seat on this grant.";
  }
  if (low === "zone_geometry_write_closed") {
    return "You can view zones, but geometry writes need a manager seat.";
  }
  if (low === "robots_admin_only") {
    return "External robots need a manager seat.";
  }
  if (/_admin_only|seat_gated|write_closed|user_floor/.test(low)) {
    return "Your account cannot open this Fleet seat. Ask a manager if you need access.";
  }
  if (/network|failed to fetch|timeout/.test(low)) {
    return "Network issue — try again in a moment.";
  }
  if (/\/api\/|\/fleet\/|GET |POST |status_path|http/.test(low)) {
    return "That action could not be completed.";
  }
  if (/^[a-z][a-z0-9_]{3,}$/.test(n) && n.includes("_")) {
    return "Your account cannot open this Fleet seat. Ask a manager if you need access.";
  }
  if (n.length > 160) return n.slice(0, 157) + "…";
  return n;
}

/** Operator seat label — never engineer codes in the chrome. */
export function softFleetShellLabelOf(seat, opts = {}) {
  if (opts.error) return "Could not confirm Fleet access · try Refresh";
  if (opts.loading) return "Confirming Fleet seat…";
  if (seat && seat.postureLabel) return String(seat.postureLabel);
  const d = String((seat && seat.detail) || "").toLowerCase();
  if (d.includes("seat_gated")) return "Fleet needs a signed-in seat";
  if (d.includes("all_sealed")) return "Fleet is closed on this seat";
  if (d.includes("tab_rail")) return "Fleet tabs closed · records may stay open";
  if (d.includes("solo")) return "Fleet tabs open · detail records closed";
  if (seat && seat.tabRailAllowed && seat.soloRecordAllowed) {
    if (seat.firmwareTabAllowed && seat.externalTabAllowed) {
      return "Fleet open · Board updates and External open";
    }
    return "Fleet open · operator seats";
  }
  if (seat && seat.tabRailAllowed) return "Fleet tabs open · detail records closed";
  if (seat && seat.soloRecordAllowed) return "Fleet tabs closed · records may stay open";
  return "Fleet is closed on this seat";
}

/** Soft≠Live Page194: missing contract ops must not crash the shell. */
function safeFleetPath(op, opts) {
  try {
    return path(op, opts);
  } catch (_e) {
    if (op === "get__fleet_status") return "/fleet/status";
    return "";
  }
}

/** Pure: seat chips from Live Fleet shell posture (Page194) — fail-closed. */
export function fleetShellSeatOf(post, opts = {}) {
  if (opts.error) {
    return {
      tabRailAllowed: false,
      firmwareTabAllowed: false,
      groupsTabAllowed: false,
      zonesTabAllowed: false,
      healthTabAllowed: false,
      diagnoseTabAllowed: false,
      externalTabAllowed: false,
      soloRecordAllowed: false,
      deviceSoloAllowed: false,
      groupSoloAllowed: false,
      crumbFleetAllowed: true,
      reReadAllowed: true,
      role: String(opts.role || ""),
      detail: "fleet_shell_status_error",
      enabled: false,
      postureLabel: softFleetShellLabelOf(null, { error: true }),
      defaultTab: "health",
      tabsOpen: 0,
    };
  }
  if (opts.loading) {
    return {
      tabRailAllowed: false,
      firmwareTabAllowed: false,
      groupsTabAllowed: false,
      zonesTabAllowed: false,
      healthTabAllowed: false,
      diagnoseTabAllowed: false,
      externalTabAllowed: false,
      soloRecordAllowed: false,
      deviceSoloAllowed: false,
      groupSoloAllowed: false,
      crumbFleetAllowed: false,
      reReadAllowed: false,
      role: String(opts.role || ""),
      detail: "fleet_shell_status_loading",
      enabled: false,
      postureLabel: softFleetShellLabelOf(null, { loading: true }),
      defaultTab: "health",
      tabsOpen: 0,
    };
  }
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const tabsOpen = Number(lim.tabs_open);
  const seat = {
    tabRailAllowed: p.tab_rail_allowed === true,
    firmwareTabAllowed: p.firmware_tab_allowed === true,
    groupsTabAllowed: p.groups_tab_allowed === true,
    zonesTabAllowed: p.zones_tab_allowed === true,
    healthTabAllowed: p.health_tab_allowed === true,
    diagnoseTabAllowed: p.diagnose_tab_allowed === true,
    externalTabAllowed: p.external_tab_allowed === true,
    soloRecordAllowed: p.solo_record_allowed === true,
    deviceSoloAllowed: p.device_solo_allowed === true,
    groupSoloAllowed: p.group_solo_allowed === true,
    crumbFleetAllowed: p.crumb_fleet_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    role: String(p.principal_role || opts.role || ""),
    detail: p.detail || null,
    enabled: p.enabled === true,
    postureLabel: String(p.posture_label || "").trim(),
    defaultTab: String(p.default_tab || "health"),
    tabsOpen: Number.isFinite(tabsOpen) && tabsOpen >= 0 ? Math.floor(tabsOpen) : 0,
  };
  seat.postureLabel = softFleetShellLabelOf(seat);
  return seat;
}

/** Pure: choose visible Live tab — never invent open tabs. */
export function fleetShellAtOf(sub, seat) {
  const s = seat || {};
  if (sub === "device") return s.deviceSoloAllowed ? "device" : "";
  if (sub === "group") return s.groupSoloAllowed ? "group" : "";
  const hit = TABS.find((t) => t.id === sub);
  if (hit && s[hit.flag]) return hit.id;
  for (const t of TABS) {
    if (s[t.flag]) return t.id;
  }
  /* Never invent a default open tab when every seat flag is sealed. */
  return "";
}

export default function Fleet({ sub, arg, toast }) {
  const posture = useApi(safeFleetPath("get__fleet_status"));
  const seat = fleetShellSeatOf(posture.data, {
    loading: !!posture.loading && !posture.data,
    error: !!posture.error && !posture.data,
  });
  const at = fleetShellAtOf(sub, seat);
  const solo = at === "device" || at === "group";
  const tabs = useMemo(
    () => TABS.filter((t) => seat[t.flag]),
    [seat.firmwareTabAllowed, seat.groupsTabAllowed, seat.zonesTabAllowed,
      seat.healthTabAllowed, seat.diagnoseTabAllowed, seat.externalTabAllowed],
  );
  const postureLabel = seat.postureLabel || softFleetShellLabelOf(seat);

  const reRead = () => {
    posture.reload && posture.reload();
    toast && toast("Fleet status refreshed", "go");
  };

  if (posture.loading && !posture.data) {
    return html`
      <div className="sheet fleet-shell fleet-shell--liquid desk-craft"
        data-cmc-page="page194-fleet-shell" data-cmc-residual="page194-shell">
        <div className="wrap">
          <p className="note">Confirming Fleet seat…</p>
        </div>
      </div>`;
  }

  /* If both buses closed after Live status, honest blank — no Soft tabs invent. */
  if (!posture.loading && !seat.tabRailAllowed && !seat.soloRecordAllowed) {
    return html`
      <div className="sheet fleet-shell fleet-shell--liquid desk-craft"
        data-cmc-page="page194-fleet-shell" data-cmc-residual="page194-shell">
        <div className="wrap">
          <${Alert} tone="bad" icon="ban" title="Fleet closed"
            say=${softFleetShellErrorOf(posture.error)
              || "Your seat cannot open Fleet tabs or device records. Ask a manager if you need access."}/>
          <p className="note">${postureLabel}</p>
          <div className="btn-row" style=${{ marginTop: 12 }}>
            <button type="button" className="btn btn--ghost btn--sm"
              disabled=${!seat.reReadAllowed || posture.loading} onClick=${reRead}>
              <${Ico} d="refresh" w=${14}/> Refresh
            </button>
          </div>
        </div>
      </div>`;
  }

  /* Solo deep-link while solo closed — honest refuse, no ghost device/group. */
  if ((sub === "device" || sub === "group") && !solo && !posture.loading) {
    return html`
      <div className="sheet fleet-shell fleet-shell--liquid desk-craft"
        data-cmc-page="page194-fleet-shell" data-cmc-residual="page194-shell">
        <div className="wrap">
          <${Alert} tone="warn" title="Detail closed"
            say="This unit or group record is closed on your seat."/>
          <div className="btn-row" style=${{ marginTop: 12 }}>
            <button type="button" className="btn btn--ghost btn--sm"
              disabled=${!seat.crumbFleetAllowed}
              onClick=${() => go("/fleet/" + (seat.defaultTab || "health"))}>
              Back to Fleet
            </button>
            <button type="button" className="btn btn--ghost btn--sm"
              disabled=${!seat.reReadAllowed || posture.loading} onClick=${reRead}>
              <${Ico} d="refresh" w=${14}/> Refresh
            </button>
          </div>
        </div>
      </div>`;
  }

  return html`
    <div className="sheet fleet-shell fleet-shell--liquid desk-craft"
      data-cmc-fleet=${at === "external" ? "page75" : undefined}
      data-cmc-page="page194-fleet-shell"
      data-cmc-residual="page194-shell"
      data-fleet-craft="v430"
      data-flat="1">
      <div className="wrap">
        ${solo ? html`
          <button type="button" className="crumb"
            disabled=${!seat.crumbFleetAllowed}
            onClick=${() => go("/fleet/groups")}>
            <${Ico} d="arrowL" w=${13}/> Fleet
          </button>` : html`
          <${F}>
            <header className="fleet-shell__head fleet-shell__head--flat">
              <div className="fleet-shell__titleline">
                <span className="fleet-shell__mark">Fleet</span>
                <h1 className="ph fleet-shell__title">Fleet</h1>
                <span className="fleet-shell__mode">Site</span>
              </div>
              <p className="psub fleet-shell__lede">
                Board firmware lives under Board updates · capability packs live on Skills warehouse.
              </p>
            </header>

            <div className="fleet-shell__kpis" role="group" aria-label="Fleet seats">
              <div className="fleet-shell__kpi" data-on=${seat.tabRailAllowed ? "1" : "0"}>
                <span>Tabs</span>
                <b>${seat.tabRailAllowed ? "open" : "closed"}</b>
              </div>
              <div className="fleet-shell__kpi" data-on=${seat.soloRecordAllowed ? "1" : "0"}>
                <span>Records</span>
                <b>${seat.soloRecordAllowed ? "open" : "closed"}</b>
              </div>
              <div className="fleet-shell__kpi" data-on=${seat.firmwareTabAllowed ? "1" : "0"}>
                <span>Board updates</span>
                <b>${seat.firmwareTabAllowed ? "open" : "closed"}</b>
              </div>
              <div className="fleet-shell__kpi" data-on=${seat.enabled ? "1" : "0"}>
                <span>Seat</span>
                <b>${seat.role === "sa_staff" ? "staff"
                  : seat.role === "superadmin" ? "root"
                  : seat.role === "admin" ? "manager"
                  : seat.role === "user" ? "operator"
                  : (seat.role || "—")}</b>
              </div>
            </div>
            <p className="note">${postureLabel}</p>
            ${posture.error ? html`
              <${Alert} tone="warn" title="Could not refresh Fleet"
                say=${softFleetShellErrorOf(posture.error)}/>` : null}

            <div className="btn-row fleet-shell__acts">
              <button type="button" className="btn btn--ghost btn--sm desk-craft__act"
                disabled=${!seat.reReadAllowed || posture.loading} onClick=${reRead}>
                <${Ico} d="refresh" w=${14}/> Refresh
              </button>
              <button type="button" className="btn btn--ghost btn--sm desk-craft__act"
                onClick=${() => go("/edge/skills")}>
                Skills warehouse
              </button>
              ${seat.firmwareTabAllowed
                ? html`<button type="button" className="btn btn--ghost btn--sm desk-craft__act"
                    onClick=${() => go("/ota/base")}>
                    Board updates
                  </button>`
                : null}
            </div>

            <nav className="fleet-shell__nav fleet-shell__nav--flat" aria-label="Fleet seats">
              ${tabs.map((t) => html`
                <button key=${t.id} type="button" role="tab"
                  className="fleet-shell__tab"
                  data-on=${at === t.id ? "1" : "0"}
                  aria-selected=${at === t.id ? "true" : "false"}
                  onClick=${() => go("/fleet/" + t.id)}>
                  <span>${t.label}</span>
                </button>`)}
              ${!tabs.length ? html`
                <span className="note">No tabs open on this seat.</span>` : null}
            </nav>
          <//>`}

        <${AnimatePresence} mode="wait">
          <${M.div} key=${at + (arg || "")} initial=${{ opacity: 0 }} animate=${{ opacity: 1 }}
              exit=${{ opacity: 0 }} transition=${{ duration: 0.1, ease: [0.2, 0, 0.2, 1] }}
              style=${{ display: "flex", flexDirection: "column", gap: 14 }}>
            ${at === "firmware" && seat.firmwareTabAllowed && html`<${FleetFirmwareDesk} toast=${toast}/>`}
            ${at === "groups" && seat.groupsTabAllowed && html`<${FleetGroupsDesk} toast=${toast}/>`}
            ${at === "zones" && seat.zonesTabAllowed && html`<${FleetZonesDesk} toast=${toast}/>`}
            ${at === "health" && seat.healthTabAllowed && html`<${Health} toast=${toast}/>`}
            ${at === "diagnose" && seat.diagnoseTabAllowed && html`<${Diagnose} toast=${toast}/>`}
            ${at === "external" && seat.externalTabAllowed && html`<${ExternalFleetPane} toast=${toast}/>`}
            ${at === "device" && seat.deviceSoloAllowed && html`<${Device} id=${arg} toast=${toast}/>`}
            ${at === "group" && seat.groupSoloAllowed && html`<${GroupPage} name=${arg} toast=${toast}/>`}
            ${!at ? html`<${Alert} tone="warn" title="Fleet not ready"
              say="Tabs and records are closed for this seat."/>` : null}
          <//>
        <//>
      </div>
    </div>`;
}

/* Soft≠Live Page75: liquid ExternalFleetDesk (Page40 Live buses retained).
   Sandpit double Alert + bare Refresh deleted — rail / KPI live in the desk. */
function ExternalFleetPane({ toast }) {
  return html`<${ExternalFleetDesk} toast=${toast}/>`;
}

/* Soft≠Live Page73: FleetFirmwareDesk lives in fleet-firmware-desk.js (mounted above).
   Soft Save rollback/switches/stall theatre deleted. */

/* ── fleet health ───────────────────────────────────────────────────────
   Soft≠Live Page69: Live register + global-metrics desk (SSE preferred).
   Soft healthBlocks invented counters deleted — blanks until Live. */
function Health({ toast }) {
  return html`<${FleetHealthDesk} toast=${toast}/>`;
}

/* ── why offline ────────────────────────────────────────────────────────
   Soft≠Live Page68: Live quiet list + presence-diagnose desk.
   Soft timing/damping save theatre + Soft toast buckets deleted. */
function Diagnose({ toast }) {
  return html`<${FleetDiagnoseDesk} toast=${toast}/>`;
}
